import { withCsrfHeader } from '../../csrf';
import { API_BASE, type RequestFn } from '../http';
import type {
  IdentityCorrectionRequest,
  IdentityCorrectionResult,
  IdentityFieldsResponse,
  InfraActionDetail,
  InfraArchiveEntry,
  InfraArchiveKind,
  InfraChangeResult,
  InfraCompany,
  InfraConfigType,
  InfraCustomerWithData,
  InfraCreateRequest,
  InfraCreateResult,
  InfraDevice,
  InfraDevicePort,
  InfraDevicePortInput,
  InfraWorkplaceUser,
  InfraWorkplaceUserInput,
  InfraWorkplaceAssignmentInput,
  InfraWorkplaceSoftware,
  InfraWorkplaceSoftwareInput,
  InfraHistoryEntry,
  InfraRollbackResult,
  InfraSite,
  InfraWritableField,
  InfrastructureResponse,
  WizardCommitRequest,
  WizardCommitResult,
  WizardContext,
  WizardData,
  WizardPreview,
  WizardPreviewRequest,
} from '../types';

export function createInfrastructureApi(request: RequestFn) {
  return {
    async getInfrastructureCompanies() {
      return request<{ companies: InfraCompany[] }>('/infrastructure/companies');
    },

    async getInfrastructureCustomersWithData() {
      return request<{ customers: InfraCustomerWithData[] }>('/infrastructure/companies-with-data');
    },

    async getCompanyInfrastructure(
      companyId: string,
      options?: {
        types?: string[];
        site?: string;
        includeInactive?: boolean;
      }
    ) {
      const params = new URLSearchParams();
      if (options?.types && options.types.length > 0) {
        params.set('types', options.types.join(','));
      }
      if (options?.site) params.set('site', options.site);
      if (options?.includeInactive) params.set('includeInactive', 'true');
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<InfrastructureResponse>(
        `/infrastructure/companies/${companyId}/configurations${query}`
      );
    },

    /** Archiv einer Firma — ausgemustertes, das noch zurückholbar ist. */
    async getInfraArchive(companyId: string) {
      return request<{ entries: InfraArchiveEntry[]; retentionDays: number }>(
        `/infrastructure/companies/${companyId}/archive`
      );
    },

    /** Legt einen bereits deaktivierten Eintrag ins Archiv. */
    async archiveInfraEntry(companyId: string, kind: InfraArchiveKind, id: number) {
      return request<{ ok: true; name: string; dependents: number }>(
        `/infrastructure/companies/${companyId}/archive`,
        { method: 'POST', body: JSON.stringify({ kind, id }) }
      );
    },

    /** Holt einen archivierten Eintrag zurück — er ist danach wieder „Inaktiv". */
    async restoreInfraEntry(companyId: string, kind: InfraArchiveKind, id: number) {
      return request<{ ok: true; name: string }>(
        `/infrastructure/companies/${companyId}/archive/restore`,
        { method: 'POST', body: JSON.stringify({ kind, id }) }
      );
    },

    async searchInfrastructureConfigurations(
      search: string,
      options?: {
        companyId?: number;
        types?: string[];
        pageSize?: number;
      }
    ) {
      const params = new URLSearchParams({ q: search });
      if (options?.companyId) params.set('companyId', String(options.companyId));
      if (options?.types && options.types.length > 0) {
        params.set('types', options.types.join(','));
      }
      if (options?.pageSize) params.set('pageSize', String(options.pageSize));
      return request<{ configurations: InfraDevice[]; total: number }>(
        `/infrastructure/configurations/search?${params.toString()}`
      );
    },

    async getInfrastructureConfiguration(configId: number) {
      return request<{ configuration: unknown }>(`/infrastructure/configurations/${configId}`);
    },

    async getInfrastructureTypes() {
      return request<{ types: InfraConfigType[] }>('/infrastructure/types');
    },

    async getInfraWritableFields(entityType: 'ROOM' | 'WORKPLACE' | 'DEVICE') {
      return request<{ fields: InfraWritableField[] }>(
        `/infrastructure/writable-fields/${entityType}`
      );
    },

    async createInfraConfiguration(data: InfraCreateRequest) {
      return request<InfraCreateResult>('/infrastructure/configurations', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async updateInfraConfiguration(
      configId: number,
      data: {
        entityType: 'ROOM' | 'WORKPLACE' | 'DEVICE';
        changes: Record<string, unknown>;
        reason?: string;
        dryRun?: boolean;
      }
    ) {
      return request<InfraChangeResult>(`/infrastructure/configurations/${configId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async getInfraConfigHistory(configId: number, days?: number) {
      const params = days ? `?days=${days}` : '';
      return request<{ history: InfraHistoryEntry[] }>(
        `/infrastructure/configurations/${configId}/history${params}`
      );
    },

    async getInfraCompanyHistory(companyId: string, days?: number, limit?: number) {
      const params = new URLSearchParams();
      if (days) params.set('days', String(days));
      if (limit) params.set('limit', String(limit));
      const query = params.toString() ? `?${params.toString()}` : '';
      return request<{ history: InfraHistoryEntry[] }>(
        `/infrastructure/companies/${companyId}/history${query}`
      );
    },

    async rollbackInfraAction(actionId: string, reason: string) {
      return request<InfraRollbackResult>(`/infrastructure/actions/${actionId}/rollback`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
    },

    async getInfraActionDetails(actionId: string) {
      return request<{ action: InfraActionDetail }>(`/infrastructure/actions/${actionId}`);
    },

    async getIdentityFieldsInfo() {
      return request<IdentityFieldsResponse>('/infrastructure/identity-fields');
    },

    async applyIdentityCorrection(configId: number, data: IdentityCorrectionRequest) {
      return request<IdentityCorrectionResult>(
        `/infrastructure/configurations/${configId}/identity-correction`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
    },

    async getOnboardingContext(companyId: string) {
      return request<WizardContext>(`/infrastructure/onboarding/context/${companyId}`);
    },

    async getOnboardingData(companyId: string, siteId: number, includeInactive = false) {
      const params = includeInactive ? '?includeInactive=true' : '';
      return request<WizardData>(
        `/infrastructure/onboarding/data/${companyId}/${siteId}${params}`
      );
    },

    async previewOnboardingChanges(data: WizardPreviewRequest) {
      return request<WizardPreview>('/infrastructure/onboarding/preview', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async commitOnboardingChanges(data: WizardCommitRequest) {
      return request<WizardCommitResult>('/infrastructure/onboarding/commit', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    async getSuggestedName(
      entityType: 'ROOM' | 'WORKPLACE' | 'DEVICE',
      parentName: string,
      deviceCategory?: string,
      existingNames?: string[]
    ) {
      const params = new URLSearchParams({
        entityType,
        parentName,
        ...(deviceCategory && { deviceCategory }),
        ...(existingNames && { existingNames: JSON.stringify(existingNames) }),
      });
      return request<{ suggestedName: string }>(
        `/infrastructure/onboarding/suggest-name?${params.toString()}`
      );
    },

    async setSiteLabel(companyId: string, siteKey: string, label: string) {
      return request<{ ok: boolean }>(
        `/infrastructure/companies/${companyId}/site-label`,
        { method: 'PUT', body: JSON.stringify({ siteKey, label }) }
      );
    },

    // ============================================
    // SITES (echte InfraSite-Entitäten)
    // ============================================

    async getInfraSites(companyId: string) {
      return request<{ sites: InfraSite[] }>(
        `/infrastructure/companies/${companyId}/sites`
      );
    },

    async createInfraSite(
      companyId: string,
      data: { name: string; address?: string; notes?: string; displayOrder?: number }
    ) {
      return request<{ site: InfraSite }>(
        `/infrastructure/companies/${companyId}/sites`,
        { method: 'POST', body: JSON.stringify(data) }
      );
    },

    async updateInfraSite(
      companyId: string,
      siteId: number,
      data: { name?: string; address?: string; notes?: string; displayOrder?: number; active?: boolean }
    ) {
      return request<{ site: InfraSite }>(
        `/infrastructure/companies/${companyId}/sites/${siteId}`,
        { method: 'PUT', body: JSON.stringify(data) }
      );
    },

    /**
     * Erzeugt das Infrastruktur-PDF (Übersicht + optionales Diagramm-Bild +
     * Tabelle) und gibt es als Blob zum Download zurück. Eigener fetch, da die
     * Antwort binär (application/pdf) ist und nicht durch den JSON-Client passt.
     */
    async exportInfrastructurePdf(companyId: string, diagramImage?: string): Promise<Blob> {
      const res = await fetch(`${API_BASE}/infrastructure/companies/${companyId}/export-pdf`, {
        method: 'POST',
        credentials: 'include',
        headers: withCsrfHeader('POST', { 'Content-Type': 'application/json' }),
        body: JSON.stringify(diagramImage ? { diagramImage } : {}),
      });
      if (!res.ok) {
        let msg = `PDF-Export fehlgeschlagen (HTTP ${res.status})`;
        try {
          const j = (await res.json()) as { error?: string };
          if (j?.error) msg = j.error;
        } catch {
          /* keine JSON-Fehlermeldung */
        }
        throw new Error(msg);
      }
      return res.blob();
    },

    // ============================================
    // SERVER- / NETZWERK-PDF-EXPORTE
    // ============================================

    async exportServerOverviewPdf(companyId: string): Promise<Blob> {
      return exportPdfBlob(`${API_BASE}/infrastructure/companies/${companyId}/export-servers-pdf`);
    },

    async exportNetworkPdf(companyId: string): Promise<Blob> {
      return exportPdfBlob(`${API_BASE}/infrastructure/companies/${companyId}/export-network-pdf`);
    },

    /**
     * Geräte-Etiketten ("Geräte-Karten") als PDF (8 pro A4-Seite, mit QR-Code).
     * baseUrl wird für den QR-Deep-Link ins Portal verwendet; deviceIds schränkt
     * optional auf bestimmte Geräte ein (Default: alle aktiven Geräte).
     */
    async exportDeviceLabelsPdf(
      companyId: string,
      baseUrl?: string,
      deviceIds?: number[]
    ): Promise<Blob> {
      return exportPdfBlob(
        `${API_BASE}/infrastructure/companies/${companyId}/export-device-labels-pdf`,
        { ...(baseUrl ? { baseUrl } : {}), ...(deviceIds ? { deviceIds } : {}) }
      );
    },

    /**
     * Arbeitsplatz-Karte als PDF (vollständiges A4-Datenblatt für EINEN
     * Arbeitsplatz inkl. seiner Geräte + Software, mit QR-Code).
     * baseUrl wird für den QR-Deep-Link ins Portal verwendet.
     */
    async exportWorkplaceCardPdf(
      companyId: string,
      workplaceId: number,
      baseUrl?: string
    ): Promise<Blob> {
      return exportPdfBlob(
        `${API_BASE}/infrastructure/companies/${companyId}/workplaces/${workplaceId}/export-card-pdf`,
        { ...(baseUrl ? { baseUrl } : {}) }
      );
    },

    // ============================================
    // DEVICE PORTS (Netzwerk-Port-/VLAN-Dokumentation)
    // ============================================

    async getDevicePorts(companyId: string, deviceId: number) {
      return request<{ ports: InfraDevicePort[] }>(
        `/infrastructure/devices/${deviceId}/ports?companyId=${companyId}`
      );
    },

    /** Legt einen oder mehrere Ports an (Array → Batch-Generierung). */
    async createDevicePorts(
      companyId: string,
      deviceId: number,
      ports: InfraDevicePortInput | InfraDevicePortInput[]
    ) {
      return request<{ ports: InfraDevicePort[] }>(`/infrastructure/devices/${deviceId}/ports`, {
        method: 'POST',
        body: JSON.stringify({ companyId, ports }),
      });
    },

    async updateDevicePort(companyId: string, portId: number, data: Partial<InfraDevicePortInput>) {
      return request<{ port: InfraDevicePort }>(`/infrastructure/ports/${portId}`, {
        method: 'PATCH',
        body: JSON.stringify({ companyId, ...data }),
      });
    },

    async deleteDevicePort(companyId: string, portId: number) {
      return request<{ ok: boolean }>(
        `/infrastructure/ports/${portId}?companyId=${companyId}`,
        { method: 'DELETE' }
      );
    },

    /** Setzt/entfernt das übergeordnete Gerät (z. B. Switch → Netzwerkschrank). */
    async setDeviceParent(companyId: string, deviceId: number, parentDeviceId: number | null) {
      return request<{ ok: boolean }>(`/infrastructure/devices/${deviceId}/parent`, {
        method: 'PATCH',
        body: JSON.stringify({ companyId, parentDeviceId }),
      });
    },

    // ============================================
    // WORKPLACE USERS (Mitarbeiter-Stammdaten) + ZUORDNUNGEN
    // ============================================

    /** Alle Mitarbeiter eines Kunden (optional inkl. archivierter). */
    async getWorkplaceUsers(companyId: string, includeArchived = false) {
      return request<{ users: InfraWorkplaceUser[] }>(
        `/infrastructure/companies/${companyId}/workplace-users?includeArchived=${includeArchived}`
      );
    },

    async createWorkplaceUser(
      companyId: string,
      user: InfraWorkplaceUserInput,
      assignments?: InfraWorkplaceAssignmentInput[]
    ) {
      return request<{ user: InfraWorkplaceUser }>(`/infrastructure/companies/${companyId}/workplace-users`, {
        method: 'POST',
        body: JSON.stringify({ ...user, assignments }),
      });
    },

    async updateWorkplaceUser(companyId: string, userId: number, data: Partial<InfraWorkplaceUserInput>) {
      return request<{ user: InfraWorkplaceUser }>(`/infrastructure/workplace-users/${userId}`, {
        method: 'PATCH',
        body: JSON.stringify({ companyId, ...data }),
      });
    },

    /** Archiviert (active=false) bzw. reaktiviert einen Mitarbeiter firmenweit. */
    async setWorkplaceUserActive(companyId: string, userId: number, active: boolean) {
      return request<{ user: InfraWorkplaceUser }>(`/infrastructure/workplace-users/${userId}/archive`, {
        method: 'POST',
        body: JSON.stringify({ companyId, active }),
      });
    },

    async deleteWorkplaceUser(companyId: string, userId: number) {
      return request<{ ok: boolean }>(
        `/infrastructure/workplace-users/${userId}?companyId=${companyId}`,
        { method: 'DELETE' }
      );
    },

    /** Ordnet einen Mitarbeiter einem Arbeitsplatz zu (idempotent). */
    async assignWorkplaceUser(companyId: string, userId: number, input: InfraWorkplaceAssignmentInput) {
      return request<{ ok: boolean }>(`/infrastructure/workplace-users/${userId}/assignments`, {
        method: 'POST',
        body: JSON.stringify({ companyId, ...input }),
      });
    },

    async updateWorkplaceAssignment(
      companyId: string,
      assignmentId: number,
      data: { isPrimary?: boolean; note?: string | null }
    ) {
      return request<{ ok: boolean }>(`/infrastructure/workplace-assignments/${assignmentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ companyId, ...data }),
      });
    },

    async unassignWorkplaceUser(companyId: string, assignmentId: number) {
      return request<{ ok: boolean }>(
        `/infrastructure/workplace-assignments/${assignmentId}?companyId=${companyId}`,
        { method: 'DELETE' }
      );
    },

    // ============================================
    // WORKPLACE SOFTWARE (Software-Dokumentation je Arbeitsplatz)
    // ============================================

    async getWorkplaceSoftware(companyId: string, workplaceId: number) {
      return request<{ software: InfraWorkplaceSoftware[] }>(
        `/infrastructure/workplaces/${workplaceId}/software?companyId=${companyId}`
      );
    },

    async createWorkplaceSoftware(
      companyId: string,
      workplaceId: number,
      software: InfraWorkplaceSoftwareInput | InfraWorkplaceSoftwareInput[]
    ) {
      return request<{ software: InfraWorkplaceSoftware[] }>(`/infrastructure/workplaces/${workplaceId}/software`, {
        method: 'POST',
        body: JSON.stringify({ companyId, software }),
      });
    },

    async updateWorkplaceSoftware(companyId: string, softwareId: number, data: Partial<InfraWorkplaceSoftwareInput>) {
      return request<{ software: InfraWorkplaceSoftware }>(`/infrastructure/workplace-software/${softwareId}`, {
        method: 'PATCH',
        body: JSON.stringify({ companyId, ...data }),
      });
    },

    async deleteWorkplaceSoftware(companyId: string, softwareId: number) {
      return request<{ ok: boolean }>(
        `/infrastructure/workplace-software/${softwareId}?companyId=${companyId}`,
        { method: 'DELETE' }
      );
    },
  };
}

/**
 * Gemeinsamer Helper für binäre PDF-Exporte (application/pdf). Eigener fetch, da die
 * Antwort nicht durch den JSON-Client passt. Verhält sich wie exportInfrastructurePdf.
 */
async function exportPdfBlob(url: string, body: unknown = {}): Promise<Blob> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: withCsrfHeader('POST', { 'Content-Type': 'application/json' }),
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    let msg = `PDF-Export fehlgeschlagen (HTTP ${res.status})`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch {
      /* keine JSON-Fehlermeldung */
    }
    throw new Error(msg);
  }
  return res.blob();
}
